import { AxiosPromise } from 'axios';

import { URLS } from '@/constants';
import { PurchaseActivity } from '@/models/activities';
import { PaginatedResponse } from '@/models/api';
import { Campaign } from '@/models/campaign';
import { BulkAddCartItemsResponse, Cart, CartPlacedOrder } from '@/models/cart';
import { Company, PaymentOption } from '@/models/company';
import { DiscoverProduct } from '@/models/discover';
import { BrandProductLine } from '@/models/product';
import {
  BasicCustomer,
  BrandMenu,
  BrandMenuEmailData,
  ExportMenuCustomOptions,
  ExportMenuPayload,
  ExportMenuResponse,
  ExportOrderFormCustomOptions,
  ExpressMenuBrandParams,
  ExpressMenuOrderParams,
  ExpressMenuPaymentOptionsParams,
  ExpressMenuProductsParams,
  FavoritesItem,
  FavoritesList,
  GuestCartItemsParams,
  GuestCartsParams,
  PublicPromoCode,
  PurchaseHistoryItem,
  ShopBrandApiResponse,
  ShopProduct,
  ShopProductsFilters,
  StoreFavoritesItem,
} from '@/models/shop';
import { SponsorshipResponse } from '@/models/sponsorship';
import { useUserStore } from '@/piniaStores/user/user.store';
import { fetchBrandContent } from '@/services/api/content';
import { parseBrandMenus, parseProduct } from '@/services/parsers/shop';
import session, { apiSession, apiSlugSession, createSession, slugSession } from '@/services/session';
import { buildPath } from '@/utils';
import JSONTransform from '@/utils/json-transform';

import { CompanyAllowedToPurchaseBrand } from './models/brandMenu';
import { InternalPreviewProduct } from './models/previewProduct';
import { PublicMenuBrand, PublicMenuProduct } from './models/publicMenu';
import {
  ExpressMenuBrand,
  ExpressMenuProductsResponse,
  GetBrandBySlugResponse,
  GetUserCompaniesByEmailResponse,
  ShopProductDetails,
} from './models/shop';

/**
 * Get all Products
 * @param params - Serialized GET params
 * @param [options] - optional arguments
 * @param [options.requestId] - used for canceling requests through the requestManager
 */
export const getProducts = async (
  params: string,
  options?: {
    requestId?: string;
  },
): Promise<PaginatedResponse<ShopProduct>> => {
  const { requestId } = options || {};

  const url = `${URLS.SHOP.PRODUCTS}/?${URLS.CUSTOM_ERROR}&${params}`;
  const response = await apiSlugSession.get(url, {
    requestId,
  });

  return response?.data;
};

/**
 * Get all Product Filter and sort options
 * @return {Promise<ShopProductsFilters>}
 */
export const getProductFilters = async (): Promise<ShopProductsFilters> => {
  const filters = [
    'categories',
    'product_status',
    'product_strain_classification',
    'flower_package_size',
    'units_of_measure',
    'shop_product_sort_options',
    'shop_product_purchase_history',
    'thc_units',
  ];

  const params = [URLS.CUSTOM_ERROR, ...filters.map((filter) => `filters=${filter}`)].join('&');

  const response = await apiSlugSession.get(`${URLS.SHOP.PRODUCT_FILTERS}/?${params}`);

  return response.data;
};

/**
 * Get menu brand(s), product lines, and products
 * @param {Number}: brandId The brand ID
 */
export const getBrandMenus = async ({ id, params = '' }: { id: number | string; params?: string }) => {
  const url = `${URLS.SHOP.BRAND.BASE}/${id}/${URLS.SHOP.BRAND.PRODUCTS.BASE}?product_lines=1${params && `&${params}`}`;

  const response = await apiSlugSession.get<BrandMenu[]>(url, {
    requestId: 'brandMenus[]',
  });

  let data = response?.data;

  if (data) {
    // TODO: clean-up with updated backend response (follow-up)
    data = data?.length
      ? parseBrandMenus(data)
      : ([
          {
            brand: {
              id,
              name: '',
            },
            productLines: [],
          },
        ] as BrandMenu[]);
  }

  return data;
};

/**
 * TODO: Remove with Preview Menu restructure
 * Get branded products by brand id.
 * @param {Number}: brandId The brand ID
 */
export const getBrandedProducts = async (brandId) => {
  const url = `${URLS.SHOP.BRAND.BASE}/${brandId}/${URLS.SHOP.BRAND.PRODUCTS.BASE}`;
  const response = await apiSlugSession.get(url);

  return response.data.map(parseProduct);
};

/**
 * Get details in reference to a product
 * @param {Number} brandId The brand ID
 * @param {Number} productId The product ID
 * @param {String} [companySlug] Optionaly passed company slug
 */
export const getProductDetail = async (brandId: number, productId: number, companySlug?: string) => {
  const userStore = useUserStore();
  const slug = companySlug ? companySlug : userStore.userActiveCompany?.slug;
  const url = `${slug}/${URLS.SHOP.BRAND.BASE}/${brandId}/${URLS.SHOP.BRAND.PRODUCTS.BASE}/${productId}`;

  const response = await session.get<ShopProductDetails>(url);

  return parseProduct(response.data);
};

/**
 * Gets product details for the available product ID
 */
export const getProductDetailsById = async (productId: string) => {
  const url = `internal/api/products/${productId}`;

  const response = await session.get<ShopProduct>(url);

  return response.data;
};

/**
 * Get the featured product for a brand
 * @param {number} brandId
 */
export const getFeaturedProduct = async (brandId: number) => {
  const url = `${URLS.SHOP.BRAND.BASE}/${brandId}/${URLS.SHOP.BRAND.FEATURED_PRODUCT}`;

  const response = await apiSlugSession.get(url);

  return parseProduct(response.data);
};

/**
 * Get the featured product preview for brand and license type
 * @param {number} brandId
 * @param {number} licenseTypeId
 */
export const getFeaturedProductPreview = async (brandId: number, licenseTypeId: number) => {
  const response = await apiSlugSession.get(
    `${URLS.SHOP.BRAND.BASE}/${brandId}/${URLS.SHOP.BRAND.PREVIEW.FEATURED_PRODUCT}/`,
    {
      params: { license_type_id: licenseTypeId },
    },
  );

  return parseProduct(response.data);
};

export const getDeals = async () => {
  const parseField = (f) => (Number.isNaN(parseFloat(f)) ? f : parseFloat(f));
  const response = await apiSlugSession.get(`${URLS.SHOP.DEALS}/`);

  response.data.forEach((product) => {
    product.discount = parseFloat(product.discount);
    product.categoryName = product.category.name;
    product.brandTrusted = product.brand.trusted;

    product.salePrice = product.salePrice
      ? {
          ...product.salePrice,
          amount: parseField(product.salePrice.amount),
        }
      : undefined;
    product.wholesalePrice = {
      ...product.wholesalePrice,
      amount: parseField(product.wholesalePrice.amount),
    };

    return product;
  });

  return response.data;
};

export const getBrands = async (): Promise<ShopBrandApiResponse[]> => {
  const response = await apiSlugSession.get<ShopBrandApiResponse[]>(`${URLS.SHOP.BRANDS}/?with_new_badges=True`);

  return response.data;
};

export const getNewBrands = async (companyId: number) => {
  return await fetchBrandContent({
    badge: ['new'],
    companyId,
  });
};

type PublicPromoPayload = {
  brand?: number;
  valid?: boolean;
};

export const getPublicPromos = async ({ brand, valid = true }: PublicPromoPayload) => {
  const response = await session.get<PaginatedResponse<PublicPromoCode>>(`${URLS.SHOP.BUYER_PROMOCODES}/`, {
    params: { brand, company_slug: undefined, limit: 1000, valid },
  });

  return response.data;
};

export const getSponsorships = async (sponsorshipPosition) => {
  const response = await session.get<SponsorshipResponse[]>(`${URLS.SHOP.SPONSORSHIPS}/`, {
    params: { position: sponsorshipPosition },
  });

  return response.data;
};

export const getImpressionPixel = async (impressionUrl: string) => {
  const impressionSession = createSession('');
  const response = await impressionSession.get(impressionUrl);

  return response;
};

/**
 * Retrieve brand info
 * @param {number} brandId
 * @param {boolean} isPreview
 */
export const getBrand = async (brandId: number, isPreview: boolean) => {
  let url = `${URLS.SHOP.BRAND.BASE}/${brandId}`;

  if (isPreview) {
    url += `/${URLS.SHOP.BRAND.PREVIEW.BASE}`;
  }

  const response = await apiSlugSession.get(url);

  return response.data;
};

interface GetBrandBySlugArgs {
  brandSlug: string;
}

/**
 * Returns a brand ID from a brand slug
 */
export async function getBrandBySlug({ brandSlug }: GetBrandBySlugArgs) {
  const url = buildPath(URLS.SHOP.BRAND.BRAND_BY_SLUG, { brandSlug });
  const response = await apiSession.get<GetBrandBySlugResponse>(url);

  return response.data;
}

interface GetCompaniesAllowedToPurchaseBrandArgs {
  brandSlug: string;
}

/**
 * Returns a list of the user's companies that are allowed to purchase from a given brand.
 */
export async function getCompaniesAllowedToPurchaseBrand({ brandSlug }: GetCompaniesAllowedToPurchaseBrandArgs) {
  const url = buildPath(URLS.SHOP.BRAND.COMPANIES_ALLOWED_TO_PURCHASE_BRAND, { brandSlug });
  const response = await apiSession.get<CompanyAllowedToPurchaseBrand[]>(url);

  return response.data;
}

export function exportMenu(payload: ExportMenuPayload) {
  return session.post<ExportMenuResponse>(`${URLS.SHOP.BRAND.MENU_EXPORT}/`, payload);
}

/**
 * Create a sample request
 */
// eslint-disable-next-line require-await
export async function createSampleRequest(payload: { productId: number; message: string; quantity: number }) {
  return apiSlugSession.post(`${URLS.SHOP.SAMPLE_REQUESTS}/`, {
    product: payload.productId,
    message: payload.message,
    quantity: payload.quantity,
  });
}

/**
 * Create a learn more request
 */
// eslint-disable-next-line require-await
export async function createLearnMoreRequest(payload: { brandId: number; sellerId: string }) {
  return apiSlugSession.post(`${URLS.SHOP.LEARN_MORE}/`, {
    brand: payload.brandId,
    seller: payload.sellerId,
  });
}

/**
 * Create a 'Coming soon' notify me request
 * @param {number} productId the id of the 'coming soon' product
 */
// eslint-disable-next-line require-await
export async function saveNotifyWhenAvailableRequest(productId: number) {
  return apiSlugSession.post(`${URLS.SHOP.NOTIFY_ME}/`, {
    product: productId,
  });
}

/**
 * Send a message to brand
 */
// eslint-disable-next-line require-await
export async function sendMessage(payload: {
  brandId?: number;
  productId?: number;
  toCompany: number;
  source: string;
  topic: string;
  message: string;
}) {
  return apiSlugSession.post(`${URLS.SHOP.SEND_MESSAGE}/`, {
    ...(payload.brandId && { brandId: payload.brandId }),
    ...(payload.productId && { productId: payload.productId }),
    toCompany: payload.toCompany,
    source: payload.source,
    topic: payload.topic,
    message: payload.message,
  });
}

/**
 * Retrieve the purchase list items for the current user
 */
export async function getPurchaseList(buyerId?: number | string): Promise<StoreFavoritesItem[]> {
  const params = buyerId ? `buyer_id=${buyerId}` : '';
  const response = await session.get(`${URLS.SHOP.PURCHASE_LIST.BASE}/?${params}`);

  return response.data;
}

/**
 * Update purchase list item
 */
export async function updatePurchaseListItem(item: StoreFavoritesItem): Promise<StoreFavoritesItem> {
  const response = await session.patch(
    `${URLS.SHOP.PURCHASE_LIST.BASE}/${URLS.SHOP.PURCHASE_LIST.ITEM}/${item.id}/?${URLS.CUSTOM_ERROR}`,
    item,
  );

  return response.data;
}

/**
 * Gets the Favorites List meta data for each of the current user's companies
 */
export async function getFavoritesLists() {
  const url = `${URLS.SHOP.FAVORITES.BASE}/`;

  const response = await session.get<FavoritesList[]>(url);

  return response.data;
}

/**
 * Creates a Favorites List for the active company (by default) or the given buyer company
 */
export async function createFavoritesList(args: { buyer: Pick<Company, 'id' | 'name'> }) {
  const { buyer } = args;
  const url = `${URLS.SHOP.FAVORITES.BASE}/`;

  const body = {
    buyer_id: buyer.id,
    name: buyer.name,
  };

  const response = await session.post<FavoritesList>(url, body);

  return response.data;
}

/**
 * Gets the the favorited items for a given favorites list id
 */
export async function getFavoritesItems(args: { listId: number }) {
  const { listId } = args;
  const url = `${URLS.SHOP.FAVORITES.BASE}/${listId}/products/`;

  const response = await session.get<FavoritesItem[]>(url);

  return response.data;
}

/**
 * Creates a new favorites item for a given favorites list id and product id
 */
export async function createFavoritesItem(args: { listId: number; productId: number }) {
  const { listId, productId } = args;
  const url = `${URLS.SHOP.FAVORITES.BASE}/${listId}/products/`;
  const body = { productId };

  const response = await session.post<FavoritesItem>(url, body);

  return response.data;
}

/**
 * Deletes a favorites item for a given favorites list id and product id
 */
export async function deleteFavoritesItem(args: { listId: number; productId: number }) {
  const { listId, productId } = args;
  const url = `${URLS.SHOP.FAVORITES.BASE}/${listId}/products/${productId}/`;

  const response = await session.delete(url);

  return response.data;
}

/**
 * Add a purchase list item to the cart
 * @param {number} purchaseListItemId
 */
export async function addPurchaseListItemToCart(purchaseListItemId: number) {
  await session.post(`${URLS.SHOP.PURCHASE_LIST.BASE}/${URLS.SHOP.PURCHASE_LIST.ADD_TO_CART}/?${URLS.CUSTOM_ERROR}`, {
    id: purchaseListItemId,
  });
}

/**
 * Add all the purchase list items with quantity > 0 to the cart.
 * @param {number} buyerId
 */
export async function bulkAddPurchaseListToCart(buyerId: number) {
  const response = await session.post(
    `${URLS.SHOP.PURCHASE_LIST.BASE}/${URLS.SHOP.PURCHASE_LIST.ADD_TO_CART}/?${URLS.CUSTOM_ERROR}`,
    {
      buyer: buyerId,
    },
  );

  return response.data;
}

/**
 * Retrieve the purchase history for the current user
 * @param [args.buyerId] - a buyer id to filter by
 * @param [args.productId] - a product id to filter by
 */
export async function getPurchaseHistory({
  buyerId,
  productId,
  sortBy,
}: {
  buyerId?: string | number;
  productId?: string | number;
  sortBy?: string;
} = {}) {
  const url = `${URLS.SHOP.PURCHASE_LIST.HISTORY}/`;
  const params = {
    limit: 500,
    days_past: 60,
    buyer_id: buyerId,
    product_id: productId,
    sort_by: sortBy,
  };

  const response = await session.get<PaginatedResponse<PurchaseHistoryItem>>(url, { params });

  return response.data;
}

/**
 * Fetches active campaign
 */
export const getActiveCampaign = async () => {
  // if there is no active campaign, we will get an empty string
  const { data: campaign } = await session.get<Campaign | ''>(`${URLS.COMPANY.ACTIVE_CAMPAIGN}/`);

  if (typeof campaign === 'string') {
    return;
  }

  return campaign;
};

/**
 * Get seller information
 * @param {number}: sellerId The seller to retrieve
 */
export const getSeller = async (sellerId: number) => {
  const response = await apiSlugSession.get(`${URLS.SHOP.VENDOR.BASE}/${sellerId}/`);

  return response.data;
};

/**
 * Get products for vendor menu
 * @param {Number}: sellerId The seller ID
 */
export const getVendorProducts = async (sellerId) => {
  const url = `${URLS.SHOP.VENDOR.BASE}/${sellerId}/${URLS.SHOP.VENDOR.PRODUCTS}?product_lines=1`;
  const response = await apiSlugSession.get(url);

  return parseBrandMenus(response.data);
};

/**
 * Get seller license types by given brand
 */

export const getSellerLicenseTypes = async (brandId: number) => {
  const userStore = useUserStore();
  const url = `${userStore.userActiveCompany?.slug}/${URLS.SHOP.BRAND.BASE}/${brandId}/${URLS.SHOP.BRAND.LICENSE_TYPES}`;
  const response = await session.get(url);

  return response.data;
};

/**
 * Get preview menu products from brand
 * @param {Number} payload.brandId: The brandId ID
 * @param {Number} payload.licenseTypeId: The license type ID
 */
export const getPreviewProducts = async (payload: { brandId: number; licenseTypeId: number | undefined }) => {
  const params = ['limit=9999'];

  if (payload.licenseTypeId) {
    params.push(`license_type_id=${payload.licenseTypeId}`);
  }

  const url = `${URLS.SHOP.BRAND.BASE}/${payload.brandId}/${URLS.SHOP.BRAND.PREVIEW.PRODUCTS}/?${params.join('&')}`;
  const response = await apiSlugSession.get(url);

  return response.data.results.map(parseProduct);
};

/**
 * Get a single product for the preview menu
 * @param {Number} payload.brandId: The brandId ID
 * @param {Number} payload.productId: The license type ID
 */
export const getPreviewProduct = async (payload: { brandId: number; productId: number }) => {
  const url = `${URLS.SHOP.BRAND.BASE}/${payload.brandId}/${URLS.SHOP.BRAND.PREVIEW.PRODUCTS}/${payload.productId}/`;
  const response = await apiSlugSession.get<InternalPreviewProduct>(url);

  return parseProduct(response.data);
};

/**
 * Update product line menu positions
 * @param {number} brandId
 * @param {BrandProductLine[]} productLinePositions
 */
export async function updateProductLineMenuPositions(brandId: number, productLinePositions: BrandProductLine[]) {
  const url = `${URLS.SHOP.BRAND.BASE}/${brandId}/${URLS.SHOP.BRAND.PREVIEW.PRODUCT_LINE_MENU_POSITION}/?${URLS.CUSTOM_ERROR}&${URLS.BULK}`;
  const response = await apiSlugSession.patch(url, productLinePositions);

  return response.data;
}

/**
 * Update product menu positions
 * @param {number} brandId
 * @param {object[]} productPositions
 * @param {number} productPositions[].id
 * @param {number} productPositions[].menuPosition
 */
export async function updateProductMenuPositions(
  brandId: number,
  productPositions: { id: number; menuPosition: number }[],
) {
  const url = `${URLS.SHOP.BRAND.BASE}/${brandId}/${URLS.SHOP.BRAND.PREVIEW.PRODUCT_MENU_POSITION}/?${URLS.CUSTOM_ERROR}&${URLS.BULK}`;
  const response = await apiSlugSession.patch(url, productPositions);

  return response.data;
}

/**
 * Fetch activities feed.
 * @param {number} page
 */
export const getActivitiesFeed = (page = 1) => {
  return slugSession
    .get<PaginatedResponse<PurchaseActivity>>(`${URLS.SHOP.ACTIVITY_FEED}/?${URLS.CUSTOM_ERROR}&page=${page}`)
    .then((response) => response.data);
};

/**
 *
 * Public Menu APIs
 *
 */

/**
 * Get the brand data for public menu
 * @param {string} brand of the public menu
 */
export async function getPublicMenuBrand(brand: string) {
  const url = `${brand}/${URLS.SHOP.PUBLIC_MENU.BRAND}/?${URLS.CUSTOM_ERROR}`;

  const response = await session.get<PublicMenuBrand>(url);

  return response.data;
}

/**
 * Get the featured product for a brand
 * @param {string} brand of the public menu
 */
export async function getPublicMenuFeaturedProduct(brand: string) {
  const url = `${brand}/${URLS.SHOP.PUBLIC_MENU.FEATURED_PRODUCT}/?${URLS.CUSTOM_ERROR}`;

  const response = await session.get(url);

  return response.data;
}

/**
 * Get public menu products
 * @param {string} [payload.brand] Brand of the public menu
 * @param {string} [payload.params] Serialized GET params from ServerListItem (or "from api-driven LLListView")
 * @return {Promise<PaginatedResponse>}
 */
export async function getPublicMenuProducts(
  brand: string,
  params: string,
): Promise<PaginatedResponse<PublicMenuProduct>> {
  const url = `${brand}/${URLS.SHOP.PUBLIC_MENU.PRODUCTS}/?${URLS.CUSTOM_ERROR}&${params}`;
  const response = await session.get(url);

  return response.data;
}

/**
 * Get details in reference to a product
 * @param {string} brand The brand
 * @param {number} productId The product ID
 */
export async function getPublicMenuProductDetail(brand: string, productId: number) {
  const url = `${brand}/${URLS.SHOP.PUBLIC_MENU.PRODUCTS}/${productId}/?${URLS.CUSTOM_ERROR}`;

  const response = await session.get(url);

  return response.data;
}

export type DiscoverType = 'never_purchased' | 'recommended';

/**
 * Get Discover products
 * @param {DiscoverType} type The type of list
 */
export async function getDiscoverProducts(type: DiscoverType | undefined) {
  const params = type ? `?discover_type=${type}` : '';
  const response = await apiSlugSession.get<DiscoverProduct[]>(`${URLS.SHOP.DISCOVER}/${params}`);

  return response.data;
}

/**
 * Gets all buyers who can add a specified product to cart
 * @param {number} brandID The brand id
 * @param {number} productId The product ID
 */
export async function getAllowedBuyersForProduct(args: { brandId: number; productId: number }) {
  const { brandId, productId } = args;
  const response = await apiSlugSession.get(
    `${URLS.SHOP.BRAND.BASE}/${brandId}/${URLS.SHOP.BRAND.PRODUCTS.BASE}/${productId}/${URLS.SHOP.BRAND.PRODUCTS.BUYERS}`,
  );

  return response.data;
}

export interface EmailShareMenuParams {
  companySlug: string;
  linkText: string;
  message: string;
  recipients: Array<{
    companySlug: string;
    email: string;
    name: string;
  }>;
  senderEmail: string;
  senderName: string;
}

export interface EmailBrandMenuParams extends EmailShareMenuParams, ExportMenuCustomOptions {
  brandId: number;
  licenseType: number | null;
}

export interface EmailExpressMenuParams extends EmailShareMenuParams, ExportOrderFormCustomOptions {
  expressMenuSlug: string;
}

export const emailExpressMenu = async (params: EmailExpressMenuParams) => {
  const { expressMenuSlug, ...otherParams } = params;
  const url = buildPath(URLS.SHOP.EXPRESS_MENU.EMAIL_MENU, { menuSlug: expressMenuSlug });
  const response = await session.post(url, otherParams, {
    params: { error_format: 'jsonapi' },
  });

  return response.data;
};

/**
 * Send Brand Menu Email to Customer
 * @param params.brandId Brand information to be sent
 * @param params.emailData Brand Menu Email data.
 */
export const emailBrandMenu = async (params: EmailBrandMenuParams) => {
  const { brandId, ...otherParams } = params;
  const url = `${URLS.SHOP.BRAND.BASE}/${brandId}/${URLS.SHOP.BRAND.EMAIL_MENU}/`;
  const response = await session.post<BrandMenuEmailData>(url, otherParams);

  return response.data;
};

export const getUserCompaniesByEmail = async (email: string): AxiosPromise<GetUserCompaniesByEmailResponse> => {
  const url = `${URLS.SHOP.EXPRESS_MENU.USER_COMPANIES}?email=${email}&error_format=jsonapi`;

  return await session.get(url);
};

export const getExpressMenuBrand = async ({
  menuSlug,
  ...otherParams
}: ExpressMenuBrandParams): Promise<ExpressMenuBrand> => {
  const url = buildPath(URLS.SHOP.EXPRESS_MENU.BRAND, { menuSlug });
  const params = {
    error_format: 'jsonapi',
    ...new JSONTransform(otherParams).toSnakeCaseKeys(),
  };

  const response = await session.get(url, { params });

  return response.data;
};

export const getExpressMenuProducts = async ({
  menuSlug,
  ...otherParams
}: ExpressMenuProductsParams): Promise<ExpressMenuProductsResponse> => {
  const url = buildPath(URLS.SHOP.EXPRESS_MENU.PRODUCTS, { menuSlug });

  const params = {
    error_format: 'jsonapi',
    ...new JSONTransform(otherParams).toSnakeCaseKeys(),
  };

  const response = await session.get(url, { params });

  return response.data;
};

export const getGuestCarts = async ({ userId }: GuestCartsParams) => {
  const response = await session.get<Cart>(`${URLS.CARTS.GUEST_CARTS}/`, {
    params: {
      user_id: userId,
    },
  });

  return response.data;
};

export const createGuestCartItems = async ({ items, userId, bulk = true }: GuestCartItemsParams) => {
  const response = await session.post<BulkAddCartItemsResponse>(`${URLS.CARTS.GUEST_CARTS_ITEMS}/`, items, {
    params: { bulk, user_id: userId, error_format: 'jsonapi' },
  });

  return response.data;
};

export const clearGuestCartItems = async ({ userId }: { userId: number }) => {
  return await session.post(`${URLS.CARTS.GUEST_CARTS_CLEAR}/`, null, {
    params: { user_id: userId, error_format: 'jsonapi' },
  });
};

export const createExpressMenuOrder = async ({ orderDetails, userId }: ExpressMenuOrderParams) => {
  const response = await session.post<CartPlacedOrder[]>(
    `${URLS.CARTS.GUEST_CHECKOUT}/`,
    { orderDetails },
    {
      params: {
        user_id: userId,
        error_format: 'jsonapi',
      },
    },
  );

  return response.data;
};

export const getExpressMenuPaymentOptions = async ({ customerId }: ExpressMenuPaymentOptionsParams) => {
  const response = await session.get<PaymentOption[]>(`${URLS.SHOP.EXPRESS_MENU.PAYMENT_OPTIONS}/${customerId}`);

  return response.data;
};

export const getBasicCustomers = async () => {
  const response = await session.get<BasicCustomer[]>(`${URLS.SHOP.EXPRESS_MENU.BASIC_CUSTOMERS}/`);

  return response.data;
};
